import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';
import {
  createDocument,
  updateDocument,
  deleteDocument,
  getDocumentsQueryKey,
  getDocumentDetailQueryKey,
  type CreateDocumentInput,
  type UpdateDocumentInput,
} from '../api/documents.client.js';

export function useDocumentMutations(documentId?: string) {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  const createMutation = useMutation({
    mutationFn: (input: CreateDocumentInput) => createDocument(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getDocumentsQueryKey(workspaceId) });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateDocumentInput) => updateDocument(documentId!, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getDocumentsQueryKey(workspaceId) });
      if (documentId) {
        queryClient.refetchQueries({
          queryKey: getDocumentDetailQueryKey(workspaceId, documentId),
          type: 'active',
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteDocument(documentId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getDocumentsQueryKey(workspaceId) });
      if (documentId) {
        queryClient.removeQueries({ queryKey: getDocumentDetailQueryKey(workspaceId, documentId) });
      }
    },
  });

  return { createMutation, updateMutation, deleteMutation };
}
